import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, LayoutGrid, PlusCircle, ListMusic, Pencil, ArrowLeftRight, X } from "lucide-react";
import api, { eventCoverUrl, eventVisualUrl, withToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocationPicker } from "@/components/LocationPicker";
import { ProgramBuilder } from "@/components/ProgramBuilder";
import { toast } from "sonner";
import { dateLocale } from "@/lib/i18n";

const MAIN_CATEGORIES = [
  { k: "repetition", l: "cat.repetition", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  { k: "prestation", l: "cat.prestation", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
  { k: "autre", l: "cat.autre", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
];
const LEGACY_MAIN = { concert: "prestation", concours: "prestation", evenement: "autre" };
const mainOf = (k) => (MAIN_CATEGORIES.some((c) => c.k === k) ? k : LEGACY_MAIN[k] || "autre");
const catCls = (k) => MAIN_CATEGORIES.find((c) => c.k === mainOf(k)).cls;
const mainLabel = (k) => MAIN_CATEGORIES.find((c) => c.k === mainOf(k)).l;
const CAT_STRIPE = { repetition: "border-l-emerald-500", prestation: "border-l-rose-500", autre: "border-l-sky-500" };
const PIPEBAND_AXES = ["ENSEMBLE", "DRUM CORPS", "PIPE", "SNARE", "TENOR", "BASS"];
const todayStr = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = { sub: "", title: "", date: "", end_date: "", start_time: "", end_time: "", location: "", geo: null, notes: "", dress_code: "", dress_items: [], setlist_id: "none", checklist: [], program: [], program_name: "", program_mode: "repetition", recurrent: false, freq: "weekly", until: "" };

export default function AgendaPage() {
  const { user, group, t, lang } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [setlists, setSetlists] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [invItems, setInvItems] = useState([]);
  const [myAv, setMyAv] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [progEv, setProgEv] = useState(null);
  const [program, setProgram] = useState([]);
  const [progName, setProgName] = useState("");
  const [progMode, setProgMode] = useState("repetition");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [onlyUnanswered, setOnlyUnanswered] = useState(false);
  const [params, setParams] = useSearchParams();
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverRemoved, setCoverRemoved] = useState(false);
  const [coverDefaultAsk, setCoverDefaultAsk] = useState(null);

  const load = useCallback(() => {
    api.get("/events")
      .then((r) => setEvents(r.data))
      .catch((e) => toast.error(e.friendly))
      .finally(() => setLoading(false));
    api.get("/setlists").then((r) => setSetlists(r.data)).catch(() => {});
    api.get("/library/tree").then((r) => setNodes(r.data)).catch(() => {});
    api.get("/inventory").then((r) => setInvItems(r.data.items)).catch(() => {});
    api.get("/presence").then((r) => {
      const mine = {};
      Object.entries(r.data.availability).forEach(([eid, users]) => {
        if (users[user.id]) mine[eid] = users[user.id];
      });
      setMyAv(mine);
    }).catch(() => {});
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  // Ouverture directe depuis la fiche événement : /agenda?edit=<id> ou /agenda?program=<id>
  useEffect(() => {
    const editId = params.get("edit");
    const progId = params.get("program");
    if ((!editId && !progId) || !events.length) return;
    const ev = events.find((e) => e.id === (editId || progId));
    if (ev) {
      if (editId) openEditForm(ev);
      else openProgram(ev);
    }
    const next = new URLSearchParams(params);
    next.delete("edit");
    next.delete("program");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const nodeMap = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const pieces = useMemo(() => nodes.filter((n) => n.kind === "piece"), [nodes]);
  const subs = useMemo(() => group?.event_subcategories || [], [group]);
  const groupAxes = useMemo(() => (group?.program_axes?.length ? group.program_axes : group?.style === "pipeband" ? PIPEBAND_AXES : []), [group]);
  const formMain = form.sub ? form.sub.split("::")[0] : null;
  const dressChoices = useMemo(() => invItems.filter((it) => it.family === "tenue" || form.dress_items.includes(it.id)), [invItems, form.dress_items]);

  const now = todayStr();
  const effEnd = (e) => e.end_date || e.date; // un événement multi-jours reste "à venir" jusqu'à sa fin
  const matchFilter = (e) => filterCat === "all" || mainOf(e.category) === filterCat;
  const upcoming = events.filter((e) => effEnd(e) >= now && matchFilter(e) && (!onlyUnanswered || !myAv[e.id]));
  const past = events.filter((e) => effEnd(e) < now && matchFilter(e)).reverse();
  const unansweredCount = events.filter((e) => effEnd(e) >= now && matchFilter(e) && !myAv[e.id]).length;

  // Regroupement par périodes : cette semaine / semaine prochaine / semaines datées (lundi → dimanche)
  const startOfWeek = (ds) => {
    const d = new Date(ds + "T00:00:00");
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
  };
  const weekKey = (ds) => {
    const d = startOfWeek(ds);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const periodLabel = (wk) => {
    if (wk === weekKey(now)) return t("agd.this_week");
    const n = startOfWeek(now);
    n.setDate(n.getDate() + 7);
    if (wk === weekKey(n.toISOString().slice(0, 10))) return t("agd.next_week");
    const d1 = new Date(wk + "T00:00:00");
    const d2 = new Date(d1);
    d2.setDate(d2.getDate() + 6);
    const loc = dateLocale(lang);
    if (d1.getMonth() === d2.getMonth()) {
      return `${d1.getDate()} – ${d2.getDate()} ${d2.toLocaleDateString(loc, { month: "long", year: "numeric" })}`;
    }
    return `${d1.getDate()} ${d1.toLocaleDateString(loc, { month: "short" }).replace(".", "")} – ${d2.getDate()} ${d2.toLocaleDateString(loc, { month: "long", year: "numeric" })}`;
  };
  const groupedUpcoming = useMemo(() => {
    const groups = [];
    const byKey = {};
    upcoming.forEach((ev) => {
      const wk = weekKey(ev.date);
      if (!byKey[wk]) { byKey[wk] = { key: wk, events: [] }; groups.push(byKey[wk]); }
      byKey[wk].events.push(ev);
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, filterCat, onlyUnanswered, myAv]);

  const save = async () => {
    const [main, subLabel] = form.sub ? form.sub.split("::") : ["", ""];
    const payload = {
      title: form.title.trim(),
      date: form.date,
      end_date: form.end_date || null,
      start_time: form.start_time,
      end_time: form.end_time,
      location: form.location,
      geo: form.geo,
      notes: form.notes,
      dress_code: form.dress_code,
      dress_items: form.dress_items,
      category: main,
      subcategory: subLabel,
      setlist_id: form.setlist_id === "none" ? null : form.setlist_id,
      checklist: form.checklist,
      program: mainOf(main) === "repetition" ? form.program : null,  // programme sur mesure (répétitions)
      program_name: mainOf(main) === "repetition" ? form.program_name : null,
      program_mode: mainOf(main) === "repetition" ? form.program_mode : null,
      recurrence: !editing && form.recurrent && form.until ? { freq: form.freq, until: form.until } : null,
    };
    try {
      let evId = editing?.id;
      if (editing) {
        await api.put(`/events/${editing.id}`, payload);
        toast.success(t("agd.updated"));
      } else {
        const r = await api.post("/events", payload);
        evId = r.data.id;
        toast.success(t(form.recurrent ? "agd.created_recurrent" : "agd.created"));
      }
      // Photo / affiche : upload après création (l'id est nécessaire), retrait si demandé
      if (coverFile && evId) {
        const fd = new FormData();
        fd.append("file", coverFile);
        await api.post(`/events/${evId}/cover`, fd).catch((e) => toast.error(e.friendly));
        // Création : si la catégorie n'a pas encore d'image par défaut, proposer d'enregistrer celle-ci
        if (!editing) {
          try {
            const chk = await fetch(withToken(`${API}/group/category-image/${mainOf(main)}`));
            if (chk.status === 404) setCoverDefaultAsk({ cat: mainOf(main), file: coverFile });
          } catch {}
        }
      } else if (editing && coverRemoved && evId) {
        await api.delete(`/events/${evId}/cover`).catch((e) => toast.error(e.friendly));
      }
      setFormOpen(false); setEditing(null); setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const openEditForm = (ev) => {
    setEditing(ev);
    const autoTitle = ev.subcategory || "";
    const customTitle = ev.title && ev.title !== autoTitle && !ev.title.startsWith("Répétition") ? ev.title : "";
    setForm({ sub: ev.subcategory ? `${mainOf(ev.category)}::${ev.subcategory}` : "", title: customTitle, date: ev.date, end_date: ev.end_date || "", start_time: ev.start_time, end_time: ev.end_time, location: ev.location, geo: ev.geo || null, notes: ev.notes || "", dress_code: ev.dress_code || "", dress_items: ev.dress_items || [], setlist_id: ev.setlist_id || "none", checklist: ev.checklist || [], program: ev.program || [], program_name: ev.program_name || "", program_mode: ev.program_mode || "repetition", recurrent: false, freq: "weekly", until: "" });
    setCoverFile(null);
    setCoverRemoved(false);
    setCoverPreview(ev.cover ? eventCoverUrl(ev.id, ev.cover.updated_at) : null);
    setFormOpen(true);
  };

  const openProgram = (ev) => {
    setProgEv(ev);
    setProgram(ev.program || []);
    setProgName(ev.program_name || ev.title || "");
    setProgMode(ev.program_mode || "repetition");
  };

  const saveProgram = async () => {
    try {
      await api.put(`/events/${progEv.id}/program`, { program, program_name: progName, program_mode: progMode });
      toast.success(t("agd.program_saved"));
      setProgEv(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  // Carte compacte (NIVEAU 1) : vignette date, type, titre, horaire, lieu, état de réponse discret.
  // Le détail complet vit dans la fiche événement (NIVEAU 2) — la carte entière est cliquable.
  const CompactCard = ({ ev, isPast }) => {
    const d = new Date(ev.date + "T00:00:00");
    const my = myAv[ev.id];
    const statusLabel = my ? t(`avail.${my}`) : t("agd.unanswered_state");
    return (
      <button
        onClick={() => navigate(`/agenda/${ev.id}`)}
        className={`w-full app-card p-3.5 flex items-center gap-3.5 text-left hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors ${isPast ? "opacity-50" : ""}`}
        data-testid={`event-card-${ev.id}`}
      >
        <div
          className={`w-14 shrink-0 rounded-xl border border-app border-l-4 ${CAT_STRIPE[mainOf(ev.category)]} relative overflow-hidden flex flex-col items-center py-2`}
          style={eventVisualUrl(ev) ? { backgroundImage: `url(${eventVisualUrl(ev)})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
          data-testid={`event-vignette-${ev.id}`}
        >
          {eventVisualUrl(ev) && <div className="absolute inset-0 bg-black/65" />}
          <span className="relative text-[10px] font-bold uppercase tracking-wider t-secondary" style={eventVisualUrl(ev) ? { color: "#fff" } : undefined}>
            {d.toLocaleDateString(dateLocale(lang), { month: "short" }).replace(/\./g, "")}
          </span>
          <span className="relative font-heading text-2xl font-bold leading-none mt-0.5" style={eventVisualUrl(ev) ? { color: "#fff" } : undefined}>{d.getDate()}</span>
        </div>
        <div className="min-w-0 flex-1">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${catCls(ev.category)}`} data-testid={`event-category-chip-${ev.id}`}>
            {ev.subcategory || t(mainLabel(ev.category))}
          </span>
          <p className="font-semibold truncate mt-1">{ev.title}</p>
          <p className="text-xs t-secondary truncate capitalize">
            {d.toLocaleDateString(dateLocale(lang), { weekday: "long" })}
            {ev.end_date && ` → ${new Date(ev.end_date + "T00:00:00").toLocaleDateString(dateLocale(lang), { weekday: "short", day: "numeric", month: "short" })}`}
            {ev.start_time && ` • ${ev.start_time}`}{ev.end_time && ` – ${ev.end_time}`}
            {ev.location && ` • ${ev.location}`}
          </p>
        </div>
        {!isPast && (
          <span
            className={`shrink-0 h-3.5 w-3.5 rounded-full ${my ? { yes: "bg-emerald-400", maybe: "bg-amber-400", no: "bg-rose-400" }[my] : "border-2 border-amber-400"}`}
            title={statusLabel}
            aria-label={statusLabel}
            data-testid={`event-mystatus-${ev.id}`}
          />
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold">{t("nav.agenda")}</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/presence")}
            className="h-10 w-10 rounded-xl border border-app t-secondary hover:text-white hover:bg-white/5 transition-colors inline-flex items-center justify-center"
            title={t("agd.presence_shortcut")}
            aria-label={t("agd.presence_shortcut")}
            data-testid="agenda-presence-shortcut"
          >
            <LayoutGrid className="h-5 w-5" />
          </button>
          {isAdmin && (
            <button
              className="h-10 w-10 rounded-full brand-bg text-white inline-flex items-center justify-center shadow-lg hover:brightness-110 transition"
              onClick={() => { setEditing(null); setForm(EMPTY_FORM); setCoverFile(null); setCoverPreview(null); setCoverRemoved(false); setFormOpen(true); }}
              title={t("agd.new_event")}
              aria-label={t("agd.new_event")}
              data-testid="create-event-button"
            >
              <Plus className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="agenda-filter">
        {[{ k: "all", l: "agd.filter_all" }, ...MAIN_CATEGORIES].map((c) => (
          <button
            key={c.k}
            onClick={() => setFilterCat(c.k)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              filterCat === c.k ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"
            }`}
            data-testid={`agenda-filter-${c.k}`}
          >
            {t(c.l)}
          </button>
        ))}
        <button
          onClick={() => setOnlyUnanswered((v) => !v)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
            onlyUnanswered ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "border-app t-secondary hover:bg-white/5"
          }`}
          data-testid="agenda-filter-unanswered"
        >
          {t("agd.filter_unanswered")}{unansweredCount > 0 ? ` (${unansweredCount})` : ""}
        </button>
      </div>

      <div className="space-y-6">
        {loading && <p className="t-secondary text-sm py-6 text-center" data-testid="agenda-loading">{t("agd.loading")}</p>}
        {!loading && upcoming.length === 0 && (
          <p className="t-secondary text-sm" data-testid="agenda-empty">{t(onlyUnanswered ? "agd.all_answered" : "dash.no_upcoming")}</p>
        )}
        {groupedUpcoming.map((g) => (
          <section key={g.key} data-testid={`agenda-period-${g.key}`}>
            <h3 className="text-xs font-bold uppercase tracking-wider t-secondary mb-2 px-1">{periodLabel(g.key)}</h3>
            <div className="space-y-2.5">
              {g.events.map((ev) => <CompactCard key={ev.id} ev={ev} isPast={false} />)}
            </div>
          </section>
        ))}
      </div>

      {past.length > 0 && !onlyUnanswered && (
        <>
          <h3 className="font-heading text-lg font-bold t-secondary">{t("agd.past_events")}</h3>
          <div className="space-y-2.5">
            {past.map((ev) => <CompactCard key={ev.id} ev={ev} isPast={true} />)}
          </div>
        </>
      )}

      {/* Création / édition */}
      {/* Proposition : enregistrer l'image importée comme image par défaut de la catégorie */}
      <Dialog open={!!coverDefaultAsk} onOpenChange={(v) => { if (!v) setCoverDefaultAsk(null); }}>
        <DialogContent className="app-card border-app max-w-sm" data-testid="cover-default-dialog">
          <DialogHeader><DialogTitle>{t("agd.cover_default_title")}</DialogTitle></DialogHeader>
          <p className="text-sm t-secondary">{t("agd.cover_default_q", { cat: t(mainLabel(coverDefaultAsk?.cat || "autre")) })}</p>
          <div className="flex flex-col gap-2 mt-2">
            <Button className="brand-bg text-white" data-testid="cover-default-yes" onClick={async () => {
              try {
                const fd = new FormData();
                fd.append("file", coverDefaultAsk.file);
                await api.post(`/group/category-image/${coverDefaultAsk.cat}`, fd);
                toast.success(t("agd.cover_default_saved", { cat: t(mainLabel(coverDefaultAsk.cat)) }));
              } catch (e) { toast.error(e.friendly); }
              setCoverDefaultAsk(null);
            }}>{t("agd.cover_default_yes")}</Button>
            <Button variant="outline" data-testid="cover-default-no" onClick={() => setCoverDefaultAsk(null)}>{t("agd.cover_default_no")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="card-bg border-app max-w-lg max-h-[85vh] overflow-y-auto" data-testid="event-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t(editing ? "agd.edit_event" : "agd.new_event")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.type_label")}</Label>
              <Select value={form.sub} onValueChange={(v) => setForm({ ...form, sub: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="event-category-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                <SelectContent>
                  {MAIN_CATEGORIES.map((mc) => {
                    const list = subs.filter((s) => s.main === mc.k).map((s) => s.label);
                    if (form.sub.startsWith(`${mc.k}::`) && !list.includes(form.sub.split("::")[1])) list.push(form.sub.split("::")[1]);
                    if (!list.length) return null;
                    return (
                      <SelectGroup key={mc.k}>
                        <SelectLabel className="uppercase">{t(mc.l)}</SelectLabel>
                        {list.map((l) => <SelectItem key={`${mc.k}::${l}`} value={`${mc.k}::${l}`} data-testid={`event-sub-option-${mc.k}-${l.replace(/\s/g, "-")}`}>{l}</SelectItem>)}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {form.sub && mainOf(form.sub.split("::")[0]) !== "repetition" && (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="event-title-input">{t("agd.title_label")}</Label>
                <Input
                  id="event-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={form.sub.split("::")[1] || t("agd.title_placeholder")}
                  className="mt-1 bg-transparent border-app"
                  data-testid="event-title-input"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.date")}</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="event-date-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.end_date")}</Label>
                <Input type="date" value={form.end_date} min={form.date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="event-end-date-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.start")}</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="event-start-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.end")}</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="event-end-input" />
              </div>
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.location")}</Label>
              <LocationPicker value={form.location} geo={form.geo} onChange={({ location, geo }) => setForm({ ...form, location, geo })} />
            </div>

            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.cover_label")}</Label>
              {coverPreview ? (
                <div className="mt-1 relative">
                  <img src={coverPreview} alt={t("agd.cover_label")} className="w-full max-h-44 object-cover rounded-xl border border-app" data-testid="event-cover-preview" />
                  <button
                    type="button"
                    onClick={() => { setCoverFile(null); setCoverPreview(null); setCoverRemoved(true); }}
                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 rounded-full p-1.5 transition-colors"
                    data-testid="event-cover-remove"
                    aria-label={t("agd.cover_remove")}
                    title={t("agd.cover_remove")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-1 flex items-center justify-center gap-2 border border-dashed border-app rounded-xl px-4 py-5 text-sm t-secondary hover:bg-white/5 cursor-pointer transition-colors" data-testid="event-cover-add">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="event-cover-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setCoverFile(f);
                        setCoverPreview(URL.createObjectURL(f));
                        setCoverRemoved(false);
                      }
                      e.target.value = "";
                    }}
                  />
                  <Plus className="h-4 w-4" /> {t("agd.cover_add")}
                </label>
              )}
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.notes_optional")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="event-notes-input" />
            </div>
            {["prestation", "autre"].includes(formMain) && (
              <>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.dress_label")}</Label>
                  <Input value={form.dress_code} onChange={(e) => setForm({ ...form, dress_code: e.target.value })} placeholder={t("agd.dress_placeholder")} className="mt-1 bg-transparent border-app" data-testid="event-dress-input" />
                </div>
                {dressChoices.length > 0 && (
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.dress_items_label")}</Label>
                    <div className="mt-1 grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border border-app rounded-lg p-2" data-testid="event-dress-items">
                      {dressChoices.map((it) => (
                        <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            className="accent-emerald-500"
                            checked={form.dress_items.includes(it.id)}
                            onChange={(e) => setForm({ ...form, dress_items: e.target.checked ? [...form.dress_items, it.id] : form.dress_items.filter((x) => x !== it.id) })}
                            data-testid={`dress-item-${it.id}`}
                          />
                          {it.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {!editing && (
              <div className="border border-app rounded-lg p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="accent-emerald-500" checked={form.recurrent} onChange={(e) => setForm({ ...form, recurrent: e.target.checked })} data-testid="event-recurrent-checkbox" />
                  {t("agd.recurrent")}
                </label>
                {form.recurrent && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.recurrence_label")}</Label>
                      <Select value={form.freq} onValueChange={(v) => setForm({ ...form, freq: v })}>
                        <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="event-freq-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">{t("agd.freq_daily")}</SelectItem>
                          <SelectItem value="weekly">{t("agd.freq_weekly")}</SelectItem>
                          <SelectItem value="monthly">{t("agd.freq_monthly")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.until")}</Label>
                      <Input type="date" value={form.until} onChange={(e) => setForm({ ...form, until: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="event-until-input" />
                    </div>
                  </div>
                )}
              </div>
            )}
            {mainOf(formMain || "") === "repetition" ? (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("dash.program")}</Label>
                {form.program.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!form.program_name) setForm((f) => ({ ...f, program_name: f.title || (f.sub ? f.sub.split("::")[1] : "") }));
                      setBuilderOpen(true);
                    }}
                    className="mt-1 w-full app-card p-4 text-left hover:bg-white/[0.03] transition-colors"
                    data-testid="program-create-open"
                  >
                    <span className="flex items-center gap-3 accent font-semibold text-base">
                      <PlusCircle className="h-6 w-6" /> {t("agd.program_create")}
                    </span>
                  </button>
                ) : (
                  <div className="mt-1 app-card p-3.5 flex items-center gap-3" data-testid="program-summary">
                    <span className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                      <ListMusic className="h-5 w-5 accent" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold truncate" data-testid="program-summary-name">{form.program_name || form.title || t("dash.program")}</p>
                      <p className="text-xs t-secondary" data-testid="program-summary-meta">{t("agd.program_summary", { n: form.program.length, mode: t(form.program_mode === "concert" ? "agd.mode_concert" : "agd.mode_repetition") })}</p>
                    </div>
                    <button type="button" onClick={() => setBuilderOpen(true)} className="p-2 t-secondary hover:text-white transition-colors" data-testid="program-edit-open" aria-label={t("agd.edit_program")}><Pencil className="h-4 w-4" /></button>
                    <button type="button" onClick={() => setForm({ ...form, program: [], program_name: "", program_mode: "repetition" })} className="p-2 t-secondary hover:text-white transition-colors" data-testid="program-reset" aria-label={t("agd.program_reset")}><ArrowLeftRight className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            ) : (
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.setlist_label")}</Label>
              <Select value={form.setlist_id} onValueChange={(v) => setForm({ ...form, setlist_id: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="event-setlist-select"><SelectValue placeholder={t("agd.no_setlist")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("agd.no_setlist")}</SelectItem>
                  {setlists.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            {invItems.length > 0 && mainOf(formMain || "") !== "repetition" && (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("agd.checklist_label")}</Label>
                <div className="mt-1 grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border border-app rounded-lg p-2" data-testid="event-checklist">
                  {invItems.map((it) => (
                    <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-emerald-500"
                        checked={form.checklist.includes(it.id)}
                        onChange={(e) => setForm({ ...form, checklist: e.target.checked ? [...form.checklist, it.id] : form.checklist.filter((x) => x !== it.id) })}
                        data-testid={`checklist-item-${it.id}`}
                      />
                      {it.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button className="w-full brand-bg text-white" onClick={save} disabled={!form.sub || !form.date || (form.recurrent && !form.until)} data-testid="event-save-button">
              {editing ? t("common.save") : t(form.recurrent ? "agd.create_recurrent" : "agd.create_event")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Constructeur de programme (depuis le formulaire répétition) */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="card-bg border-app max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="program-builder-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{t("agd.program_new")}</DialogTitle></DialogHeader>
          <ProgramBuilder
            program={form.program}
            setProgram={(p) => setForm({ ...form, program: p })}
            pieces={pieces}
            nodeMap={nodeMap}
            groupAxes={groupAxes}
            name={form.program_name}
            setName={(v) => setForm({ ...form, program_name: v })}
            mode={form.program_mode}
            setMode={(v) => setForm({ ...form, program_mode: v })}
          />
          <Button className="w-full accent-bg text-slate-900 font-bold h-12 rounded-xl mt-4" onClick={() => setBuilderOpen(false)} data-testid="program-builder-done">{t("agd.save_program")}</Button>
        </DialogContent>
      </Dialog>

      {/* Programme */}
      <Dialog open={!!progEv} onOpenChange={(v) => !v && setProgEv(null)}>
        <DialogContent className="card-bg border-app max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="program-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{t("agd.program_new")}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <ProgramBuilder program={program} setProgram={setProgram} pieces={pieces} nodeMap={nodeMap} groupAxes={groupAxes} name={progName} setName={setProgName} mode={progMode} setMode={setProgMode} />
            <Button className="w-full accent-bg text-slate-900 font-bold h-12 rounded-xl" onClick={saveProgram} data-testid="program-save-button">{t("agd.save_program")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
